/**
 * ZITELOU - User Edit Page
 * Page de modification d'un utilisateur avec création d'abonnement
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeftIcon,
    UserIcon,
    EnvelopeIcon,
    ShieldCheckIcon,
    CreditCardIcon,
    PlusIcon,
    CheckCircleIcon,
    PencilIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Input, Select, Loader, ConfirmModal } from '../components/Common';
import { useApi } from '../hooks';
import api from '../services/api';
import { toast } from 'react-toastify';

const UserEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { loading, execute } = useApi();

    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        isActive: true,
    });
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Subscription creation state
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
    const [subscriptionFormData, setSubscriptionFormData] = useState({
        planId: '',
        subscriptionType: 'stripe', // 'stripe' or 'manual'
        trialDays: 0,
        durationDays: 30,
        endDate: '',
    });
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);

    // Fetch user data
    const fetchUser = useCallback(async () => {
        await execute(async () => {
            const result = await api.getUserById(id);
            setUser(result);
            setFormData({
                firstname: result.firstname || '',
                lastname: result.lastname || '',
                email: result.email || '',
                isActive: result.isActive !== false,
            });
        });
    }, [id, execute]);

    // Fetch subscription plans
    const fetchSubscriptionPlans = useCallback(async () => {
        setLoadingPlans(true);
        try {
            const plans = await api.getSubscriptionPlans();
            setSubscriptionPlans(plans);
            // Si pas de plan sélectionné, on prend le premier par défaut
            if (plans.length > 0 && !subscriptionFormData.planId) {
                setSubscriptionFormData(prev => ({ ...prev, planId: plans[0].id }));
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Erreur lors du chargement des plans');
        } finally {
            setLoadingPlans(false);
        }
    }, [subscriptionFormData.planId]);

    useEffect(() => {
        fetchUser();
        fetchSubscriptionPlans();
    }, [fetchUser, fetchSubscriptionPlans]);

    // On retire l'effet qui chargeait les plans uniquement à l'ouverture du formulaire
    // useEffect(() => {
    //    if (showSubscriptionForm && subscriptionPlans.length === 0) {
    //        fetchSubscriptionPlans();
    //    }
    // }, [showSubscriptionForm, subscriptionPlans.length, fetchSubscriptionPlans]);

    // Track changes
    useEffect(() => {
        if (user) {
            const changed =
                formData.firstname !== (user.firstname || '') ||
                formData.lastname !== (user.lastname || '') ||
                formData.email !== (user.email || '') ||
                formData.isActive !== (user.isActive !== false);
            setHasChanges(changed);
        }
    }, [formData, user]);

    // Handle input changes
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Handle subscription form changes
    const handleSubscriptionChange = (field, value) => {
        setSubscriptionFormData(prev => ({ ...prev, [field]: value }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstname.trim()) {
            newErrors.firstname = 'Le prénom est requis';
        } else if (formData.firstname.trim().length < 2) {
            newErrors.firstname = 'Le prénom doit contenir au moins 2 caractères';
        }

        if (!formData.lastname.trim()) {
            newErrors.lastname = 'Le nom est requis';
        } else if (formData.lastname.trim().length < 2) {
            newErrors.lastname = 'Le nom doit contenir au moins 2 caractères';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'L\'email n\'est pas valide';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Veuillez corriger les erreurs du formulaire');
            return;
        }

        setIsSaving(true);
        try {
            await api.updateUser(id, {
                firstname: formData.firstname.trim(),
                lastname: formData.lastname.trim(),
                email: formData.email.trim(),
                isActive: formData.isActive,
            });
            toast.success('Utilisateur modifié avec succès');
            navigate(`/users/${id}`);
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la modification');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle subscription creation
    const handleCreateSubscription = async () => {
        if (!subscriptionFormData.planId) {
            toast.error('Veuillez sélectionner un plan');
            return;
        }

        setIsCreatingSubscription(true);
        try {
            if (subscriptionFormData.subscriptionType === 'manual') {
                await api.createManualSubscription(id, {
                    planId: subscriptionFormData.planId,
                    durationDays: subscriptionFormData.durationDays,
                    endDate: subscriptionFormData.endDate || undefined,
                });
            } else {
                await api.createUserSubscription(id, {
                    planId: subscriptionFormData.planId,
                    createStripe: true,
                    trialDays: subscriptionFormData.trialDays,
                });
            }

            toast.success('Abonnement créé avec succès');
            setShowSubscriptionForm(false);
            // Refresh user data
            await fetchUser();
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la création de l\'abonnement');
        } finally {
            setIsCreatingSubscription(false);
        }
    };

    // Handle cancel with confirmation if changes exist
    const handleCancel = () => {
        if (hasChanges) {
            setShowCancelModal(true);
        } else {
            navigate(`/users/${id}`);
        }
    };

    // Handle cancel confirmation
    const confirmCancel = () => {
        setShowCancelModal(false);
        navigate(`/users/${id}`);
    };

    // Check if user has an active subscription
    const hasActiveSubscription = user && 
        (user.subscription_status === 'active' || user.subscription_status === 'trialing');

    // Check if user can have a subscription created (always true now to allow modification)
    const canCreateSubscription = true;

    // Format price display
    const formatPrice = (plan) => {
        if (!plan.price && !plan.amount_cents) return 'Gratuit';
        const price = plan.price || (plan.amount_cents / 100);
        const currency = plan.currency || 'EUR';
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: currency,
        }).format(price);
    };

    if (loading || !user) {
        return <Loader fullScreen text="Chargement des données..." />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ArrowLeftIcon className="w-4 h-4" />}
                        onClick={handleCancel}
                    >
                        Retour
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            Modifier l'utilisateur
                        </h1>
                        <p className="text-gray-600 mt-1">
                            {user.firstname} {user.lastname} - {user.email}
                        </p>
                    </div>
                </div>
            </div>

            {/* Edit Form */}
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Info Card */}
                    <Card title="Informations personnelles" className="lg:col-span-2">
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Firstname */}
                                <div>
                                    <Input
                                        label="Prénom"
                                        type="text"
                                        value={formData.firstname}
                                        onChange={(e) => handleChange('firstname', e.target.value)}
                                        error={errors.firstname}
                                        placeholder="Entrez le prénom"
                                        leftIcon={<UserIcon className="w-5 h-5 text-gray-400" />}
                                    />
                                </div>

                                {/* Lastname */}
                                <div>
                                    <Input
                                        label="Nom"
                                        type="text"
                                        value={formData.lastname}
                                        onChange={(e) => handleChange('lastname', e.target.value)}
                                        error={errors.lastname}
                                        placeholder="Entrez le nom"
                                        leftIcon={<UserIcon className="w-5 h-5 text-gray-400" />}
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <Input
                                    label="Adresse email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    error={errors.email}
                                    placeholder="exemple@email.com"
                                    leftIcon={<EnvelopeIcon className="w-5 h-5 text-gray-400" />}
                                />
                            </div>
                        </div>
                    </Card>

                    {/* Status Card */}
                    <Card title="Statut du compte">
                        <div className="space-y-6">
                            {/* Active Status */}
                            <div>
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                    <ShieldCheckIcon className="w-4 h-4" />
                                    État du compte
                                </label>
                                <Select
                                    value={formData.isActive ? 'active' : 'inactive'}
                                    onChange={(e) => handleChange('isActive', e.target.value === 'active')}
                                    options={[
                                        { value: 'active', label: 'Actif' },
                                        { value: 'inactive', label: 'Inactif / Suspendu' },
                                    ]}
                                />
                                <p className="mt-2 text-sm text-gray-500">
                                    {formData.isActive
                                        ? 'L\'utilisateur peut accéder à son compte normalement.'
                                        : 'L\'utilisateur ne pourra plus se connecter à son compte.'}
                                </p>
                            </div>

                            {/* Subscription Info (Read-only) */}
                            {user.subscription_status && (
                                <div className="pt-4 border-t border-gray-200">
                                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                                        Abonnement actuel
                                    </label>
                                    <p className="text-gray-900 capitalize">
                                        {user.subscription_status === 'active' && 'Actif'}
                                        {user.subscription_status === 'expired' && 'Expiré'}
                                        {user.subscription_status === 'suspended' && 'Suspendu'}
                                        {user.subscription_status === 'cancelled' && 'Résilié'}
                                        {user.subscription_status === 'none' && 'Aucun'}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Gérez l'abonnement depuis la page de détail de l'utilisateur.
                                    </p>
                                </div>
                            )}

                            {/* User ID (Read-only) */}
                            <div className="pt-4 border-t border-gray-200">
                                <label className="text-sm font-medium text-gray-500 mb-2 block">
                                    ID Utilisateur
                                </label>
                                <p className="text-gray-900 font-mono">#{user.id}</p>
                            </div>

                            {/* Registration Date (Read-only) */}
                            {user.created_at && (
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                                        Date d'inscription
                                    </label>
                                    <p className="text-gray-900">{user.created_at}</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Subscription Creation Card */}
                <div className="mt-6">
                    <Card
                        title={
                            <div className="flex items-center gap-2">
                                <CreditCardIcon className="w-5 h-5" />
                                Gestion de l'abonnement
                            </div>
                        }
                    >
                        {/* Current subscription status */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500">Statut actuel</p>
                                    <p className="text-lg font-medium">
                                        {user.subscription_status === 'active' && (
                                            <span className="text-green-600 flex items-center gap-1">
                                                <CheckCircleIcon className="w-5 h-5" />
                                                Abonnement actif
                                            </span>
                                        )}
                                        {user.subscription_status === 'expired' && (
                                            <span className="text-orange-600">Abonnement expiré</span>
                                        )}
                                        {user.subscription_status === 'suspended' && (
                                            <span className="text-red-600">Abonnement suspendu</span>
                                        )}
                                        {user.subscription_status === 'cancelled' && (
                                            <span className="text-gray-600">Abonnement résilié</span>
                                        )}
                                        {(!user.subscription_status || user.subscription_status === 'none') && (
                                            <span className="text-gray-500">Aucun abonnement</span>
                                        )}
                                    </p>
                                </div>

                                {canCreateSubscription && !showSubscriptionForm && (
                                    <Button
                                        type="button"
                                        variant={hasActiveSubscription ? "warning" : "primary"}
                                        leftIcon={hasActiveSubscription ? <PencilIcon className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
                                        onClick={() => setShowSubscriptionForm(true)}
                                    >
                                        {hasActiveSubscription ? 'Modifier l\'abonnement' : 'Créer un abonnement'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Subscription Creation Form */}
                        {showSubscriptionForm && (
                            <div className="border-t border-gray-200 pt-6">
                                <h4 className="text-md font-medium text-gray-900 mb-4">
                                    {hasActiveSubscription ? 'Modifier l\'abonnement actuel' : 'Créer un nouvel abonnement'}
                                </h4>

                                {loadingPlans ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader text="Chargement des plans..." />
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Subscription Type */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Type d'abonnement
                                            </label>
                                            <Select
                                                value={subscriptionFormData.subscriptionType}
                                                onChange={(e) => handleSubscriptionChange('subscriptionType', e.target.value)}
                                                options={[
                                                    { value: 'stripe', label: 'Abonnement Stripe (paiement récurrent)' },
                                                    { value: 'manual', label: 'Abonnement manuel (sans paiement)' },
                                                ]}
                                            />
                                            <p className="mt-1 text-xs text-gray-500">
                                                {subscriptionFormData.subscriptionType === 'stripe'
                                                    ? 'Crée un abonnement Stripe avec facturation automatique.'
                                                    : 'Crée un abonnement sans lien Stripe (gratuit ou géré manuellement).'}
                                            </p>
                                        </div>

                                        {/* Plan Selection */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Plan d'abonnement
                                            </label>
                                            {subscriptionPlans.length === 0 ? (
                                                <p className="text-sm text-gray-500">
                                                    Aucun plan disponible. Créez d'abord des plans d'abonnement.
                                                </p>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {subscriptionPlans.map((plan) => (
                                                        <div
                                                            key={plan.id}
                                                            className={`
                                                                border rounded-lg p-4 cursor-pointer transition-all
                                                                ${subscriptionFormData.planId === plan.id
                                                                ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500'
                                                                : 'border-gray-200 hover:border-gray-300'}
                                                            `}
                                                            onClick={() => handleSubscriptionChange('planId', plan.id)}
                                                        >
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <h5 className="font-medium text-gray-900">
                                                                        {plan.name}
                                                                    </h5>
                                                                    {plan.description && (
                                                                        <p className="text-sm text-gray-500 mt-1">
                                                                            {plan.description}
                                                                        </p>
                                                                    )}
                                                                    {plan.duration_days && (
                                                                        <p className="text-xs text-gray-400 mt-1">
                                                                            Durée: {plan.duration_days} jours
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <span className="text-lg font-semibold text-gray-900">
                                                                    {formatPrice(plan)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Trial Days (for Stripe subscriptions) */}
                                        {subscriptionFormData.subscriptionType === 'stripe' && (
                                            <div>
                                                <Input
                                                    label="Jours d'essai gratuit"
                                                    type="number"
                                                    min="0"
                                                    value={subscriptionFormData.trialDays}
                                                    onChange={(e) => handleSubscriptionChange('trialDays', parseInt(e.target.value) || 0)}
                                                    placeholder="0"
                                                />
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Laissez à 0 pour démarrer immédiatement sans période d'essai.
                                                </p>
                                            </div>
                                        )}

                                        {/* Duration or End Date (for manual subscriptions) */}
                                        {subscriptionFormData.subscriptionType === 'manual' && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <Input
                                                        label="Durée (jours)"
                                                        type="number"
                                                        min="1"
                                                        value={subscriptionFormData.durationDays}
                                                        onChange={(e) => handleSubscriptionChange('durationDays', parseInt(e.target.value) || 30)}
                                                        placeholder="30"
                                                    />
                                                </div>
                                                <div>
                                                    <Input
                                                        label="Ou date de fin spécifique"
                                                        type="date"
                                                        value={subscriptionFormData.endDate}
                                                        onChange={(e) => handleSubscriptionChange('endDate', e.target.value)}
                                                    />
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Si définie, remplace la durée en jours.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setShowSubscriptionForm(false)}
                                                disabled={isCreatingSubscription}
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                onClick={handleCreateSubscription}
                                                disabled={isCreatingSubscription || !subscriptionFormData.planId}
                                                loading={isCreatingSubscription}
                                            >
                                                {isCreatingSubscription ? 'Création...' : 'Créer l\'abonnement'}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t border-gray-200">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Annuler
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isSaving || !hasChanges}
                        loading={isSaving}
                    >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                    </Button>
                </div>
            </form>

            {/* Cancel Confirmation Modal */}
            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                title="Annuler les modifications"
                message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter sans sauvegarder ?"
                confirmText="Quitter sans sauvegarder"
                confirmVariant="warning"
            />
        </div>
    );
};

export default UserEdit;
